import { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Select } from '@/components/ui/select';

import { RootState, Mode, modes } from '../../types/graph';

import { useResetQuestionnaire } from '../../hooks/useResetQuestionnaire';
import {
  setMode,
  setNoAppointmentMode,
  setConsultationTimeKnown,
  setDisplayFullQueue,
} from '../../reducers/graph';

export const Options = () => {
  const dispatch = useDispatch();
  const mode = useSelector((state: RootState) => state.graph.mode);
  const noAppointment = useSelector(
    (state: RootState) => state.consultation.noAppointment,
  );
  const consultationTimeKnown = useSelector(
    (state: RootState) => state.graph.options.CONSULTATION_TIME_KNOWN,
  );
  const displayFullQueue = useSelector(
    (state: RootState) => state.graph.options.DISPLAY_FULL_QUEUE,
  );
  const cguAccepted = useSelector((state: RootState) => state.CGUAccepted);

  const [pendingChanges, setPendingChanges] = useState<{
    mode?: Mode;
    noAppointmentMode?: boolean;
    consultationTimeKnown?: boolean;
  }>({});
  const [showResetModal, setShowResetModal] = useState(false);

  const reset = useResetQuestionnaire();

  const handleChange = (
    type: 'mode' | 'noAppointmentMode' | 'consultationTimeKnown',
    value: Mode | boolean,
  ) => {
    if (cguAccepted !== undefined) {
      setShowResetModal(true);
    } else {
      setPendingChanges((prev) => ({
        ...prev,
        [type]: value,
      }));
    }
  };

  const handleReset = () => {
    setShowResetModal(false);
    reset();
  };

  const handleApplyChanges = () => {
    if (pendingChanges.mode !== undefined) {
      dispatch(setMode(pendingChanges.mode));
    }
    if (pendingChanges.noAppointmentMode !== undefined) {
      dispatch(setNoAppointmentMode(pendingChanges.noAppointmentMode));
    }
    if (pendingChanges.consultationTimeKnown !== undefined) {
      dispatch(setConsultationTimeKnown(pendingChanges.consultationTimeKnown));
    }
    setPendingChanges({});
  };

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;

  return (
    <div className="flex flex-col gap-6 p-4 bg-[var(--dbg-bg)] text-[var(--dbg-text)]">
      <div className="flex flex-col gap-2">
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">Mode</h3>
        <Select
          value={pendingChanges.mode ?? mode}
          onChange={(e) => handleChange('mode', e.target.value as Mode)}
          className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-[var(--dbg-border)] hover:border-[var(--dbg-border-input)] focus:ring-[var(--dbg-accent)]"
        >
          {modes.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </Select>
      </div>

      <div className="flex flex-col gap-1 mt-2">
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">
          No appointment mode
        </h3>
        <div className="flex flex-row gap-2 items-center">
          <span className="text-[var(--dbg-text-secondary)] text-sm">OFF</span>
          <Switch
            checked={pendingChanges.noAppointmentMode ?? noAppointment}
            onCheckedChange={(checked) =>
              handleChange('noAppointmentMode', checked)
            }
            className="bg-[var(--dbg-border)] data-[checked]:bg-[var(--dbg-switch)]"
          />
          <span className="text-[var(--dbg-text-secondary)] text-sm">ON</span>
        </div>
      </div>

      <div className="flex flex-col gap-1 mt-2">
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">
          Consultation time known
        </h3>
        <div className="flex flex-row gap-2 items-center">
          <span className="text-[var(--dbg-text-secondary)] text-sm">OFF</span>
          <Switch
            checked={
              pendingChanges.consultationTimeKnown ?? consultationTimeKnown
            }
            onCheckedChange={(checked) =>
              handleChange('consultationTimeKnown', checked)
            }
            className="bg-[var(--dbg-border)] data-[checked]:bg-[var(--dbg-switch)]"
          />
          <span className="text-[var(--dbg-text-secondary)] text-sm">ON</span>
        </div>
      </div>

      <div className="flex flex-col gap-1 mt-2">
        <h3 className="text-lg font-medium text-[var(--dbg-text)]">
          Display full queue
        </h3>
        <div className="flex flex-row gap-2 items-center">
          <span className="text-[var(--dbg-text-secondary)] text-sm">OFF</span>
          <Switch
            checked={displayFullQueue}
            onCheckedChange={(checked) => {
              dispatch(setDisplayFullQueue(checked));
              localStorage.setItem('DISPLAY_FULL_QUEUE', checked.toString());
            }}
            className="bg-[var(--dbg-border)] data-[checked]:bg-[var(--dbg-switch)]"
          />
          <span className="text-[var(--dbg-text-secondary)] text-sm">ON</span>
        </div>
      </div>

      {hasPendingChanges && (
        <button
          onClick={handleApplyChanges}
          className="mb-4 mt-10 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
        >
          Apply changes
        </button>
      )}

      <Dialog open={showResetModal} onOpenChange={setShowResetModal}>
        <DialogContent className="bg-[var(--dbg-bg-secondary)] text-[var(--dbg-text)] border-[var(--dbg-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--dbg-text)]">
              Reset Required
            </DialogTitle>
            <DialogDescription className="text-[var(--dbg-text-muted)]">
              You need to reset the questionnaire before making changes.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <button
              onClick={() => setShowResetModal(false)}
              className="px-4 py-2 text-sm text-[var(--dbg-text-secondary)] hover:text-[var(--dbg-text)] cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleReset}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 cursor-pointer"
            >
              Reset
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
